import { Component, DestroyRef, effect, inject, input, model, output } from "@angular/core";
import { BottomSheetComponent } from "../bottom-sheet/bottom-sheet.component";
import { OverscrollLockService } from "../../services/overscroll-lock.service";

/**
 * Standardized bottom-sheet wrapper.
 *
 * The base `app-bottom-sheet` renders its content region with `padding: 0` and
 * requires `TemplateRef` inputs, so every caller re-creates the header markup
 * and body padding. This wrapper takes projected content instead and applies
 * the shared header + body padding, so all sheets share the same spacing.
 *
 * Usage:
 * ```html
 * <app-standard-bottom-sheet [(isOpen)]="open" title="Filters">
 *   ...content...
 * </app-standard-bottom-sheet>
 * ```
 */
@Component({
  selector: "app-standard-bottom-sheet",
  standalone: true,
  imports: [BottomSheetComponent],
  templateUrl: "./standard-bottom-sheet.component.html",
  styleUrl: "./standard-bottom-sheet.component.scss"
})
export class StandardBottomSheetComponent {
  /** Two-way open state. */
  isOpen = model<boolean>(false);
  /** Optional heading rendered in the standardized header. */
  title = input<string>("");
  /** Show the built-in left/right footer buttons. */
  hasFooter = input<boolean>(false);
  leftButtonText = input<string>("Cancel");
  rightButtonText = input<string>("Apply");
  leftButtonDisabled = input<boolean>(false);
  rightButtonDisabled = input<boolean>(false);
  preventOverlayClose = input<boolean>(false);
  /** Expand to the full available height instead of sizing to content. */
  isFullScreen = input<boolean>(false);
  /** Class applied to the sheet element, e.g. to override its slide-up transition when nested. */
  backgroundClass = input<string>("");
  testId = input<string>("app-standard-bottom-sheet");

  readonly leftButtonClick = output<void>();
  readonly rightButtonClick = output<void>();
  readonly overlayClick = output<void>();

  private readonly overscrollLock = inject(OverscrollLockService);
  private readonly lockId = crypto.randomUUID();

  constructor() {
    effect(() => this.overscrollLock.setLocked(this.lockId, this.isOpen()));
    inject(DestroyRef).onDestroy(() => this.overscrollLock.setLocked(this.lockId, false));
  }
}
